<template>
  <ContextColorProvider context="wealth">
    <TwoColumnsContainerLoader v-if="isLoading" data-testid="wealth-loader" />
    <TwoColumnsContainer v-else>
      <template #main>
        <div>
          <WealthHeading
            head="Carteiras"
            :title="customerBalance.totalBalanceFormatted"
            :subtitle="customerBalance.totalBalanceUpdatedAtFormatted"
            :is-loading="balanceLoading"
            :has-error="hasBalanceError"
            @retry="setBalances"
          />
          <Performance>
            <template #actions>
              <WealthViewActions
                :buttons="customerBalance.buttons"
                :is-customer-overdue="isOverdue"
                :is-loading="balanceLoading"
              />
            </template>
          </Performance>
        </div>
      </template>

      <template #content>
        <WealthContent
          :show-create-wallet-button="!isSuitabilityPending"
          data-testid="wealth-content"
        />
      </template>
    </TwoColumnsContainer>
  </ContextColorProvider>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import {
  TwoColumnsContainer,
  TwoColumnsContainerLoader
} from '@/modules/common/components/TwoColumnsContainer';
import { Feature } from '@/modules/common/components/Feature';
import { Performance } from './components/Performance';
import { PerformanceChart } from '@/modules/wealth/components/PerformanceChart';
import { WealthContent } from './components/WealthContent';
import { WealthChartAction } from '@/modules/wealth/components/WealthChartAction';
import { ContextColorProvider } from '@warrenbrasil/nebraska-web';
import { WealthViewActions } from './components/WealthViewActions';
import { WealthHeading } from '@/modules/wealth/components/WealthHeading';
import { WarrenModules } from '@/types/navigation';
import { isSuitabilityPending } from '@/router/helpers/shouldRedirectToSuitability';
import { getWealthHero } from '../../services/investment';
import { IWealthHeroResponse } from '../../services/investment/investment-types';

import { trackLoadPortfolios } from './track-events';

const CustomerModule = namespace('CustomerModule');

@Component({
  components: {
    TwoColumnsContainer,
    TwoColumnsContainerLoader,
    ContextColorProvider,
    WealthContent,
    WealthChartAction,
    Feature,
    WealthViewActions,
    WealthHeading,
    Performance,
    PerformanceChart
  }
})
export default class WealthView extends Vue {
  private isLoading = true;
  private hasBalanceError = false;
  private balanceLoading = true;

  customerBalance = {
    totalBalanceFormatted: '',
    totalBalanceUpdatedAtFormatted: '',
    buttons: [] as IWealthHeroResponse['buttons']
  };

  @CustomerModule.Getter('isRegulatoryStatusBlocked')
  readonly isOverdue!: boolean;

  public readonly WarrenModules = WarrenModules;

  public readonly isSuitabilityPending: boolean = isSuitabilityPending();

  private async beforeMount() {
    await this.setBalances();

    this.isLoading = false;
    trackLoadPortfolios();
  }

  private async setBalances() {
    try {
      this.balanceLoading = true;
      this.hasBalanceError = false;

      const { totalBalance, totalBalanceUpdatedAtFormatted, buttons } = await getWealthHero();
      this.customerBalance = {
        totalBalanceFormatted: totalBalance.formatted,
        totalBalanceUpdatedAtFormatted,
        buttons
      };
    } catch (error) {
      this.hasBalanceError = true;
    } finally {
      this.balanceLoading = false;
    }
  }
}
</script>

<style lang="less" scoped>
@navbar-height: 64px;

.wealth-view__warning {
  position: relative;
  margin-bottom: -@navbar-height;

  @media @tabletDown {
    @navbar-height: 56px;
    margin-bottom: -@navbar-height;
  }
}
</style>
